import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    this.addEventListener('click', this.navigateToProduct);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    const isClientSide = event.detail.data.html === null;

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);

    if (!isClientSide && event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#updateVariantLabels();

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');
  };

  /**
   * Toggle which pre-rendered variant label slot is visible. Slots are deduped
   * by content — data-variant-ids is a space-separated id list (card-labels.liquid).
   * Variants without labels have no slot: every slot ends up hidden then.
   */
  #updateVariantLabels() {
    const selectedId = this.variantPicker?.selectedOption?.dataset.variantId;
    if (!selectedId) return;
    for (const slot of this.querySelectorAll('.card-labels__slot')) {
      if (!(slot instanceof HTMLElement)) continue;
      const ids = slot.dataset.variantIds?.split(' ') ?? [];
      slot.hidden = !ids.includes(selectedId);
    }
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    if (!priceContainer) return;

    // Client-side mode (synthetic variant:update from swatch): rebuild from data-attrs.
    if (event.detail.data.html === null) {
      const v = event.detail.resource;
      if (!v) return;
      priceContainer.innerHTML = v.on_sale
        ? `<span class="price">${v.price_html}</span><s class="price--compare">${v.compare_html}</s>`
        : `<span class="price">${v.price_html}</span>`;
      return;
    }

    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);
    if (newPriceElement) {
      // Clone: morph moves nodes out of its source; the doc is shared across
      // variant:update listeners.
      morph(priceContainer, newPriceElement.cloneNode(true));
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    // Client-side mode: take URL directly from event.detail.resource.url
    if (event.detail.data.html === null) {
      const productUrl = event.detail.resource?.url;
      if (!productUrl) return;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
      if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;
      return;
    }

    const anchorElement = event.detail.data.html?.querySelector('product-card a');

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    // Client-side mode: use the resource.available flag directly.
    if (event.detail.data.html === null) {
      this.#toggleAddToCartButton(event.detail.resource?.available === true);
      return;
    }

    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Swap the single card image to the selected variant's media. Cards render
   * exactly ONE image (HTML diet) — the swatch input carries the target media
   * (data-option-media-id / data-option-media-url, a width=720 CDN URL) and we
   * rewrite src/srcset instead of toggling pre-rendered hidden slides.
   */
  #updateVariantImages() {
    const selectedOption = this.variantPicker?.selectedOption;
    const mediaId = selectedOption?.dataset.optionMediaId;
    const mediaUrl = selectedOption?.dataset.optionMediaUrl;
    if (!mediaId || !mediaUrl) return;

    const container = this.querySelector('[data-card-media] [data-slide-id]');
    if (!(container instanceof HTMLElement) || container.dataset.slideId === mediaId) return;

    const image = container.querySelector('img.product-media__image:not(.product-media__image--hover)');
    if (!(image instanceof HTMLImageElement)) return;

    container.dataset.slideId = mediaId;
    image.src = mediaUrl;
    image.srcset = [180, 360, 540, 720]
      .map((width) => `${mediaUrl.replace('width=720', `width=${width}`)} ${width}w`)
      .join(', ');
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Client-side variant change for product cards: read all needed data from
   * data-attributes on the swatch radio and dispatch a synthetic variant:update
   * (with detail.data.html === null) — no fetch.
   *
   * Card markup carries: data-variant-id, data-variant-url, data-variant-price-html,
   * data-variant-compare-html, data-on-sale, data-option-available,
   * data-first-available-or-first-variant-id (fallback for unavailable swatches).
   *
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    const clickedSwatch = event.target;

    // Active-state + selection event (client-side)
    this.updateSelectedOption(clickedSwatch);
    this.dispatchEvent(new VariantSelectedEvent({ id: clickedSwatch.dataset.optionValueId ?? '' }));

    // Resolve target variant — unavailable swatch falls back to its first-available sibling.
    // data-first-available-or-first-variant-id was always identical to data-variant-id and
    // is no longer emitted (HTML diet) — variantId IS the fallback id.
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const fallbackId = clickedSwatch.dataset.variantId;
    const isSwatchInput = clickedSwatch.name?.includes('-swatch');
    const useFallback = isSwatchInput && availableCount > 0 && fallbackId;
    const variantId = useFallback ? fallbackId : clickedSwatch.dataset.variantId;
    if (useFallback) this.pendingVariantId = fallbackId;

    const variantResource = {
      id: variantId,
      available: clickedSwatch.dataset.optionAvailable === 'true',
      url: clickedSwatch.dataset.variantUrl,
      price_html: clickedSwatch.dataset.variantPriceHtml,
      compare_html: clickedSwatch.dataset.variantCompareHtml,
      on_sale: clickedSwatch.dataset.onSale === 'true',
    };

    this.dispatchEvent(
      new VariantUpdateEvent(variantResource, this.selectedOptionId ?? '', {
        html: null,
        productId: this.dataset.productId ?? '',
        newProduct: null,
      })
    );
  }

}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
